import React, { FC } from 'react';
import ApplicationContext from '../context/context';
import ErrorBoundary from './error-boundary';
import logger from '../helpers/log';
import isSsr from '../helpers/is-ssr';
import getFilteredFiles from '../helpers/get-filtered-files';

const Loading = () => <div>loading...</div>;

type ComponentProps = {
  id: string;
  className?: string;
  module: string;
  component?: string;
  debug?: boolean;
  loading?: any;
  children?: string | JSX.Element | Function | boolean;
  [props: string]: any;
  fallback?: JSX.Element | Function;
};

const RenderComponent = ({
  id,
  component,
  env,
  className,
  Component,
  rest,
  props,
  children,
  mfer
}) => {
  if (children) {
    if (Component) {
      return (
        <ErrorBoundary id={id || (component as any)} env={env}>
          {typeof children === 'function'
            ? children({ mfer, Component })
            : children}
        </ErrorBoundary>
      );
    }

    return null;
  }

  return Component ? (
    <ErrorBoundary id={id || (component as any)} env={env}>
      <div
        data-testid={id || component}
        className={className}
        id={id || component}
      >
        {React.createElement(
          Component,
          { ...rest, ...props },
          children && typeof children === 'function'
            ? (children as any)({ mfer, Component })
            : children
        )}
      </div>
    </ErrorBoundary>
  ) : null;
};

export const MicroFEComponent: FC<ComponentProps> = ({
  id,
  className,
  component,
  module,
  debug,
  loading,
  children,
  fallback,
  ...props
}) => {
  const log = logger(
    `${[module, component, id]?.filter(Boolean).join(' - ')}`,
    debug
  );

  return (
    <ApplicationContext.Consumer>
      {({ mfer, ...rest }) => {
        const { LoadedComponents, env, shellLoading, config, ssr } = mfer;
        const ssrFile = getFilteredFiles(
          config?.files,
          x => (x as any).ssr
        )?.filter(x => x?.moduleName === module);
        const file = ssrFile && (ssrFile[0] as any)?.files?.find(x => x?.ssr);
        const Component = isSsr
          ? file && file.ssr
          : LoadedComponents &&
            LoadedComponents[module] &&
            LoadedComponents[module][component || id];
        const hasComponentBeenLoaded = !!(
          LoadedComponents &&
          Object.keys(LoadedComponents).length > 0 &&
          Component
        );

        log('mfe-component', {
          mfer,
          rest,
          props,
          id,
          component,
          module,
          hasComponentBeenLoaded,
          isSsr,
          LoadedComponents,
          fallback,
          Component
        });

        if (!hasComponentBeenLoaded && isSsr && Component) {
          return RenderComponent({
            id,
            component,
            env,
            className,
            Component,
            rest,
            props,
            children,
            mfer
          });
        }

        if (
          !hasComponentBeenLoaded &&
          loading &&
          shellLoading &&
          !Component &&
          !ssr
        ) {
          return <>{typeof loading === 'boolean' ? <Loading /> : loading}</>;
        }

        if (!hasComponentBeenLoaded && !Component && !ssr) {
          if (fallback)
            return typeof fallback === 'function' ? fallback() : fallback;

          return null;
        }

        return RenderComponent({
          id,
          component,
          env,
          className,
          Component: Component || file?.ssr,
          rest,
          props,
          children,
          mfer
        });
      }}
    </ApplicationContext.Consumer>
  );
};
